/**
 * Life4Earth V2 — Org guidelines API adapter (onboarding chunk A,
 * SCOPING_ONBOARDING_FLOW §7.9, DEV-1303).
 *
 * The Getting-started "Read this: {Org} guidelines" checklist row is a
 * POINTER to this content, never the content itself (§7.9 locked rule).
 * Acknowledging writes an org-scoped audit record (actor + timestamp +
 * content version); the material stays reachable on the org About surface.
 */

import { apiFetch } from "./client";
import { invalidateAndRefetch } from "./queryCache";

export interface CommunityGuidelinesResponse {
  communityGuid: string;
  /** Null ⇒ the org has not designated guidelines (checklist row omitted). */
  content: string | null;
  version: number;
  updatedAt: string | null;
  myAcknowledgedVersion: number | null;
  /** True when the caller has acknowledged the CURRENT version. */
  acknowledged: boolean;
  /** When the caller's latest acknowledgment was recorded — the Done view's
   *  Completed date for the guidelines row. */
  myAcknowledgedAt: string | null;
}

/** Guidelines content + the caller's acknowledgment currency. */
export function fetchCommunityGuidelines(
  communityGuid: string,
): Promise<CommunityGuidelinesResponse> {
  return apiFetch<CommunityGuidelinesResponse>(
    `/api/communities/${communityGuid}/guidelines`,
  );
}

/** Acknowledge the current guidelines version (idempotent, subject-self). */
export function acknowledgeCommunityGuidelines(
  communityGuid: string,
): Promise<CommunityGuidelinesResponse> {
  return apiFetch<CommunityGuidelinesResponse>(
    `/api/communities/${communityGuid}/guidelines/acknowledge`,
    { method: "POST" },
  );
}

/**
 * Designate / edit / clear the guidelines (community admin or sysadmin;
 * server enforces via CommunityAuth.IsUserCommunityAdmin). A content
 * change bumps the version server-side, re-arming the "Read this"
 * checklist row for every member. Empty content clears the designation.
 * Invalidates the guidelines cache so open surfaces refresh.
 */
export async function saveCommunityGuidelines(
  communityGuid: string,
  content: string,
): Promise<CommunityGuidelinesResponse> {
  const res = await apiFetch<CommunityGuidelinesResponse>(
    `/api/communities/${communityGuid}/guidelines`,
    { method: "PUT", body: JSON.stringify({ content }) },
  );
  invalidateAndRefetch(`communityGuidelines:${communityGuid}`);
  return res;
}
